import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Banknote, CalendarDays, LoaderCircle, RefreshCw } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Celebration } from "@/components/Celebration";
import { CofreSquaresGrid } from "@/components/CofreSquaresGrid";
import { GoalHeaderSummary } from "@/components/GoalHeaderSummary";
import { GoalCompletion } from "@/components/GoalCompletion";
import { GoalMotivation } from "@/components/GoalMotivation";
import { DeadlineRecalculateModal } from "@/components/DeadlineRecalculateModal";
import { GoalRepeatModal } from "@/components/GoalManagementModals";
import { SavingsHistory } from "@/components/SavingsHistory";
import { SavingsMethodModal } from "@/components/SavingsMethodModal";
import { TopBar } from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";
import { deadlineLabel, formatCurrency } from "@/lib/goals";

export default function GoalDetail() {
  const { goalId } = useParams();
  const navigate = useNavigate();
  const [goal, setGoal] = useState(null);
  const [error, setError] = useState("");
  const [updating, setUpdating] = useState(null);
  const [celebration, setCelebration] = useState("");
  const [history, setHistory] = useState([]);
  const [historyError, setHistoryError] = useState("");
  const [selectedSaving, setSelectedSaving] = useState(null);
  const [showCompletion, setShowCompletion] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showDeadlineRecalculate, setShowDeadlineRecalculate] = useState(false);
  const [showRepeat, setShowRepeat] = useState(false);

  useEffect(() => {
    api.get(`/goals/${goalId}`)
      .then((response) => {
        setGoal(response.data);
        setShowCompletion(response.data.is_complete);
      })
      .catch((requestError) => setError(formatApiError(requestError)));
  }, [goalId]);

  const loadHistory = useCallback(() => {
    setHistoryError("");
    setHistoryLoaded(false);
    api.get(`/goals/${goalId}/history`)
      .then((response) => setHistory(response.data))
      .catch((requestError) => setHistoryError(formatApiError(requestError)))
      .finally(() => setHistoryLoaded(true));
  }, [goalId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const selectSquare = (index) => {
    if (!goal || updating !== null) return;
    if (goal.is_archived || goal.status === "scheduled") {
      toast.error(goal.is_archived ? "Esta meta está arquivada." : "Esta meta começa no próximo mês.");
      return;
    }
    setSelectedSaving({ kind: "square", index, value: goal.square_values[index] });
  };

  const selectFullGoalSaving = () => {
    if (!goal || goal.remaining_cents < 1 || updating !== null) return;
    if (goal.is_archived || goal.status === "scheduled") {
      toast.error(goal.is_archived ? "Esta meta está arquivada." : "Esta meta começa no próximo mês.");
      return;
    }
    setSelectedSaving({ kind: "full_goal", value: goal.remaining_cents });
  };

  const confirmSaving = async (method) => {
    if (!goal || !selectedSaving) return;
    const { index } = selectedSaving;
    setUpdating(selectedSaving.kind === "full_goal" ? "full_goal" : index);
    try {
      const endpoint = selectedSaving.kind === "full_goal"
        ? `/goals/${goalId}/complete`
        : `/goals/${goalId}/squares/${index}/confirm`;
      const { data } = await api.post(endpoint, { method });
      setGoal(data.goal);
      if (data.saving) setHistory((items) => [data.saving, ...items]);
      if (!data.already_confirmed) {
        setCelebration(data.goal.is_complete ? "complete" : "progress");
      }
      setSelectedSaving(null);
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setUpdating(null);
    }
  };

  if (error) {
    return (
      <div data-testid="goal-detail-error-page" className="min-h-screen cofre-bg">
        <TopBar />
        <main className="mx-auto max-w-3xl px-5 py-12">
          <p data-testid="goal-detail-error" role="alert" className="rounded-xl border-2 border-red-200 bg-red-50 p-5 text-lg font-semibold text-red-800">{error}</p>
          <Link to="/painel" data-testid="goal-detail-error-back-link" className="mt-6 inline-flex min-h-[48px] items-center gap-2 rounded-xl px-3 text-lg font-bold text-emerald-800 hover:bg-emerald-50"><ArrowLeft className="h-5 w-5" /> Voltar ao painel</Link>
        </main>
      </div>
    );
  }

  if (!goal) {
    return <div data-testid="goal-detail-loading" className="grid min-h-screen place-items-center cofre-bg text-xl font-bold text-emerald-900"><LoaderCircle className="mr-3 inline h-6 w-6 animate-spin" /> Carregando seu cofrinho...</div>;
  }

  const purpose = goal.purpose_category === "Prefiro não informar"
    ? "Não informado"
    : goal.purpose_detail || goal.purpose_category;
  const goalLocked = goal.is_archived || goal.status === "scheduled";
  const lockedMessage = goal.is_archived
    ? "Esta meta está arquivada e não recebe novos depósitos."
    : "Esta meta começa no próximo mês e ficará disponível na data programada.";

  return (
    <div data-testid="goal-detail-container" className="min-h-screen cofre-bg">
      <TopBar />
      <main className="mx-auto max-w-6xl px-5 py-8 sm:py-12">
        <Link to="/metas" data-testid="goal-detail-back-link" className="inline-flex min-h-[46px] items-center gap-2 rounded-xl px-3 text-lg font-bold text-emerald-800 transition-colors hover:bg-emerald-50"><ArrowLeft className="h-5 w-5" /> Minhas metas</Link>
        <section className="mt-5 space-y-6">
          <div className="rounded-3xl border border-amber-200 bg-white p-6 shadow-sm sm:p-8">
            <p className="text-sm font-bold uppercase tracking-wide text-amber-800">{goal.is_complete ? "Conquista desbloqueada" : "Seu desafio"}</p>
            <h1 data-testid="goal-detail-title" className="mt-2 font-heading text-4xl font-extrabold text-emerald-950">{goal.title}</h1>
            <p data-testid="goal-detail-amount" className="mt-5 text-3xl font-extrabold text-slate-800">{formatCurrency(goal.amount_cents)}</p>
            <p data-testid="goal-detail-purpose" className="mt-2 text-lg text-slate-600">{purpose}</p>
            {goalLocked && <p data-testid="goal-locked-notice" className="mt-4 rounded-xl border-2 border-amber-200 bg-amber-50 p-3 text-base font-bold text-amber-950">{lockedMessage}</p>}
            <p data-testid="goal-detail-deadline" className="mt-3 flex items-center gap-2 text-lg text-slate-700"><CalendarDays className="h-5 w-5 text-amber-700" /> {deadlineLabel(goal)}</p>
            <button
              type="button"
              onClick={() => setShowDeadlineRecalculate(true)}
              disabled={goal.marked_count > 0 || goalLocked}
              data-testid="recalculate-deadline-button"
              className="mt-5 inline-flex min-h-[48px] items-center gap-2 rounded-xl border-2 border-emerald-200 bg-white px-4 text-lg font-bold text-emerald-900 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className="h-5 w-5" /> Refazer cálculo do prazo
            </button>
            <p data-testid="recalculate-deadline-note" className="mt-2 text-base text-slate-600">
              Disponível antes do primeiro depósito para manter seu histórico correto.
            </p>
            <button
              type="button"
              onClick={selectFullGoalSaving}
              disabled={goal.remaining_cents < 1 || updating !== null || goalLocked}
              data-testid="save-full-goal-button"
              className="mt-4 inline-flex min-h-[52px] items-center gap-2 rounded-xl bg-amber-300 px-4 text-lg font-bold text-emerald-950 shadow-[0_4px_0_#d97706] transition-[transform,box-shadow,background-color] hover:bg-amber-400 active:translate-y-1 active:shadow-[0_1px_0_#d97706] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Banknote className="h-5 w-5" /> Guardar todo o valor que falta
            </button>
          </div>
          <GoalHeaderSummary
            goal={goal}
            depositCount={historyLoaded ? history.length : null}
          />
          <GoalMotivation goal={goal} />
          {goal.is_complete && !showCompletion && (
            <button
              type="button"
              onClick={() => setShowCompletion(true)}
              data-testid="view-completion-button"
              className="inline-flex min-h-[48px] items-center rounded-xl bg-amber-200 px-4 text-lg font-bold text-emerald-950 transition-colors hover:bg-amber-300"
            >
              Ver minha conquista
            </button>
          )}
          <CofreSquaresGrid goal={goal} updating={updating} onSelect={selectSquare} locked={goalLocked} />
          <SavingsHistory items={history} error={historyError} />
        </section>
      </main>
      {celebration && <Celebration kind={celebration} onClose={() => {
        setCelebration("");
        if (goal.is_complete) setShowCompletion(true);
      }} />}
      {selectedSaving && <SavingsMethodModal saving={selectedSaving} onClose={() => setSelectedSaving(null)} onConfirm={confirmSaving} />}
      {showCompletion && !celebration && historyLoaded && <GoalCompletion goal={goal} history={history} onClose={() => setShowCompletion(false)} onRepeat={() => {
        setShowCompletion(false);
        setShowRepeat(true);
      }} />}
      {showDeadlineRecalculate && <DeadlineRecalculateModal goal={goal} onClose={() => setShowDeadlineRecalculate(false)} onUpdated={setGoal} />}
      {showRepeat && <GoalRepeatModal goal={goal} onClose={() => setShowRepeat(false)} onScheduled={(nextGoal, alreadyScheduled) => {
        toast.success(alreadyScheduled ? "Esta meta já está preparada para o próximo mês." : "Meta preparada para o próximo mês.");
        setShowRepeat(false);
        setShowCompletion(false);
        navigate(`/metas/${nextGoal.id}`);
      }} />}
    </div>
  );
}